import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { ArrowRight, Rocket, Mail, Check } from "lucide-react";
import { cn } from "@/lib/utils";
import { posthog } from "@/lib/posthog";
import { submitToHubSpot } from "@/lib/hubspot";
import { useToast } from "@/hooks/use-toast";
import { QUIZ_QUESTIONS, calculateScore, getResultTier } from "@/lib/quiz-data";
import { z } from "zod";

const emailSchema = z.object({
  email: z.string().email("Please enter a valid email address"),
});

export const SustainabilityQuiz = () => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [showResults, setShowResults] = useState(false);
  const [showEmailCapture, setShowEmailCapture] = useState(false);
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [emailSubmitted, setEmailSubmitted] = useState(false);

  const totalQuestions = QUIZ_QUESTIONS.length;
  const progress = ((currentQuestion + 1) / totalQuestions) * 100;
  const score = calculateScore(answers);
  const resultTier = getResultTier(score);

  const handleAnswer = (questionId: string, answerValue: string) => {
    const newAnswers = { ...answers, [questionId]: answerValue };
    setAnswers(newAnswers);

    // Track answer in PostHog
    if (posthog.__loaded) {
      posthog.capture('quiz_answer_selected', {
        question: questionId,
        answer: answerValue,
        question_number: currentQuestion + 1,
      });
    }

    // Move to next question or show results
    if (currentQuestion < totalQuestions - 1) {
      setTimeout(() => setCurrentQuestion(currentQuestion + 1), 300);
    } else {
      setTimeout(() => {
        setShowResults(true);
        
        // Track quiz completion
        if (posthog.__loaded) {
          posthog.capture('quiz_completed', {
            score,
            tier: resultTier,
          });
        }

        if ((window as any).dataLayer) {
          (window as any).dataLayer.push({
            event: 'quiz_completed',
            quiz_score: score,
            quiz_tier: resultTier,
          });
        }
      }, 300);
    }
  };

  const handleSkip = () => {
    if (currentQuestion < totalQuestions - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      setShowResults(true);
    }

    if (posthog.__loaded) {
      posthog.capture('quiz_question_skipped', {
        question: QUIZ_QUESTIONS[currentQuestion],
        question_number: currentQuestion + 1,
      });
    }
  };

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      emailSchema.parse({ email });
    } catch (error) {
      toast({
        title: "Invalid email",
        description: "Please enter a valid email address",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      // Submit to HubSpot with quiz results
      await submitToHubSpot({
        email,
        firstName: '',
        lastName: '',
        company: '',
        jobTitle: '',
        companySize: '',
        quiz_score: score,
        quiz_tier: resultTier,
        quiz_completed: true,
        source: 'sustainability_quiz',
      });

      // Track in PostHog
      if (posthog.__loaded) {
        posthog.capture('quiz_email_captured', {
          score,
          tier: resultTier,
          email,
        });
      }

      if ((window as any).dataLayer) {
        (window as any).dataLayer.push({
          event: 'quiz_email_captured',
          quiz_tier: resultTier,
        });
      }

      setEmailSubmitted(true);
      
      toast({
        title: "Success! 🎉",
        description: "Check your inbox for your personalised sustainability report.",
      });

    } catch (error) {
      console.error('Quiz email submission error:', error);
      toast({
        title: "Oops!",
        description: "Something went wrong. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Quiz Questions View
  if (!showResults) {
    const questionKey = QUIZ_QUESTIONS[currentQuestion];

    return (
      <div className="w-full max-w-2xl mx-auto p-6 space-y-8">
        {/* Header */}
        <div className="text-center space-y-2">
          <h2 className="font-clash text-2xl md:text-3xl font-bold text-foreground">
            {t("quiz.title")}
          </h2>
          <p className="text-muted-foreground">
            {t("quiz.subtitle")}
          </p>
        </div>

        {/* Progress Bar */}
        <div className="space-y-2">
          <div className="flex justify-between text-sm text-muted-foreground">
            <span>{t("quiz.progress", { current: currentQuestion + 1, total: totalQuestions })}</span>
            <span>{Math.round(progress)}%</span>
          </div>
          <Progress value={progress} className="h-2" />
        </div>

        {/* Question */}
        <div className="space-y-4 animate-fade-in">
          <div className="space-y-2">
            <h3 className="text-xl font-semibold text-foreground">
              {t(`quiz.questions.${questionKey}.text`)}
            </h3>
            <p className="text-sm text-muted-foreground italic">
              {t(`quiz.questions.${questionKey}.hint`)}
            </p>
          </div>

          {/* Answer Options */}
          <div className="space-y-3">
            {['a', 'b', 'c', 'd'].map((option) => (
              <button
                key={option}
                onClick={() => handleAnswer(questionKey, option)}
                className={cn(
                  "w-full text-left p-4 rounded-lg border-2 transition-all",
                  "hover:border-brand-teal hover:bg-brand-teal/5",
                  "focus:outline-none focus:ring-2 focus:ring-brand-teal focus:ring-offset-2",
                  answers[questionKey] === option && "border-brand-teal bg-brand-teal/10"
                )}
              >
                <span className="font-medium">
                  {t(`quiz.questions.${questionKey}.options.${option}`)}
                </span>
              </button>
            ))}
          </div>

          {/* Skip Button */}
          <div className="flex justify-center pt-4">
            <button
              onClick={handleSkip}
              className="text-sm text-muted-foreground hover:text-foreground transition-colors underline-offset-4 hover:underline"
            >
              {t("quiz.skipButton")}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Results View
  if (!showEmailCapture) {
    return (
      <div className="w-full max-w-3xl mx-auto p-6 space-y-8 animate-fade-in">
        {/* Results Header */}
        <div className="text-center space-y-4">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-brand-teal to-brand-purple mb-4">
            <span className="text-4xl font-bold text-white">{score}</span>
          </div>
          
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground uppercase tracking-wide">
              {t(`quiz.results.${resultTier}.score`)}
            </p>
            <h2 className="font-clash text-3xl md:text-4xl font-bold text-foreground">
              {t(`quiz.results.${resultTier}.title`)}
            </h2>
            <p className="text-xl text-muted-foreground">
              {t(`quiz.results.${resultTier}.subtitle`)}
            </p>
          </div>
        </div>

        {/* Results Message */}
        <div className="bg-muted/30 rounded-lg p-6 space-y-4">
          <p className="text-foreground leading-relaxed">
            {t(`quiz.results.${resultTier}.message`)}
          </p>

          {/* Quick Wins */}
          <div className="space-y-3 pt-4">
            <h3 className="font-semibold text-foreground">
              {t(`quiz.results.${resultTier}.quickWins.title`)}
            </h3>
            <ul className="space-y-2">
              {[0, 1, 2].map((i) => (
                <li key={i} className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-brand-teal shrink-0 mt-0.5" />
                  <span className="text-foreground/90">
                    {t(`quiz.results.${resultTier}.quickWins.items.${i}`)}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* CTA */}
        <div className="text-center space-y-4">
          <Button
            onClick={() => setShowEmailCapture(true)}
            size="lg"
            className="group"
          >
            <Mail className="mr-2 w-5 h-5" />
            {t("quiz.emailCapture.cta")}
          </Button>
          
          <p className="text-sm text-muted-foreground">
            {t(`quiz.results.${resultTier}.ctaSubtext`)}
          </p>
        </div>
      </div>
    );
  }

  // Email Capture View
  return (
    <div className="w-full max-w-md mx-auto p-6 space-y-6 animate-fade-in">
      {emailSubmitted ? (
        <div className="text-center space-y-4">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-brand-teal/20 mb-4">
            <Check className="w-8 h-8 text-brand-teal" />
          </div>
          <h3 className="font-clash text-2xl font-bold text-foreground">
            Check Your Inbox! 📧
          </h3>
          <p className="text-muted-foreground">
            Your personalised sustainability report is on its way.
          </p>
          <Button
            onClick={() => window.location.href = '/'}
            variant="outline"
            size="lg"
          >
            Back to Homepage
          </Button>
        </div>
      ) : (
        <>
          <div className="text-center space-y-2">
            <h3 className="font-clash text-2xl font-bold text-foreground">
              {t("quiz.emailCapture.title")}
            </h3>
            <p className="text-muted-foreground">
              {t("quiz.emailCapture.subtitle")}
            </p>
          </div>

          <form onSubmit={handleEmailSubmit} className="space-y-4">
            <Input
              type="email"
              placeholder={t("quiz.emailCapture.placeholder")}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={isSubmitting}
              required
            />

            <Button
              type="submit"
              size="lg"
              className="w-full group"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                "Sending..."
              ) : (
                <>
                  <Rocket className="mr-2 w-5 h-5 transition-transform group-hover:-translate-y-1" />
                  {t("quiz.emailCapture.cta")}
                </>
              )}
            </Button>

            <p className="text-xs text-muted-foreground text-center">
              {t("quiz.emailCapture.privacy")}
            </p>
          </form>
        </>
      )}
    </div>
  );
};
